// Category-to-color default mapping (used as fallback when no custom color is set).
export const CATEGORY_COLORS = {
  industry: "#1e3a8a", // deep navy blue
  product: "#2563eb",  // medium blue
  service: "#475569",  // slate gray
  support: "#64748b",  // lighter slate gray
};

// Distinct colors for C-levels so assignments are clearly identifiable.
export const SELISE_CSUITE_COLORS = [
  "#5f7d72", // green-gray (CPO)
  "#0f766e", // teal
  "#9a3412", // burnt orange
  "#6d28d9", // violet
  "#a16207", // amber/brown
  "#be185d", // rose
  "#166534", // forest green
  "#1e40af", // royal blue
];

export function getCategoryColor(category) {
  return CATEGORY_COLORS[category] || "#475569";
}

export function getSeliseCSuiteColor(index) {
  return SELISE_CSUITE_COLORS[index % SELISE_CSUITE_COLORS.length];
}

// ---- Per-theme color storage ----
// The `color` entity field stores a JSON string: {"selise":"#hex","current":"#hex","dark":"#hex"}
// Legacy: a plain hex string is treated as the color for ALL themes (backward compat).

/**
 * Returns the raw hex for the given theme (no fallback).
 * Empty string if not set.
 */
export function getThemeColorRaw(colorField, theme) {
  if (!colorField) return "";
  const trimmed = colorField.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      return parsed[theme] || "";
    } catch {
      return "";
    }
  }
  // Legacy plain hex → applies to all themes
  return colorField;
}

/**
 * Returns the color for the given theme, falling back to `fallback` if not set.
 */
export function getThemeColor(colorField, theme, fallback = "#475569") {
  const raw = getThemeColorRaw(colorField, theme);
  return raw || fallback;
}

/**
 * Sets the color for a single theme, preserving other themes.
 * Returns the updated JSON string (or "" if all themes are cleared).
 */
export function setThemeColor(colorField, theme, hex) {
  let parsed = {};
  const trimmed = (colorField || "").trim();
  if (trimmed.startsWith("{")) {
    try { parsed = JSON.parse(trimmed); } catch { parsed = {}; }
  } else if (trimmed) {
    // Legacy plain hex → seed all themes with it
    parsed = { selise: trimmed, current: trimmed, dark: trimmed };
  }
  if (hex && hex.trim()) {
    parsed[theme] = hex.trim();
  } else {
    delete parsed[theme];
  }
  return Object.keys(parsed).length > 0 ? JSON.stringify(parsed) : "";
}

// ---- Theme config helpers (per-theme badge + background colors) ----
// JSON structure: {"selise": {"badgeColor": "#hex", "bgColor": "#hex"}, "current": {...}, "dark": {...}}

export function parseBadgeColors(badgeColorsStr) {
  if (!badgeColorsStr) return {};
  try {
    return JSON.parse(badgeColorsStr);
  } catch {
    return {};
  }
}

export function getBadgeColor(config, theme) {
  return config?.[theme]?.badgeColor || null;
}

export function setBadgeColor(config, theme, color) {
  const updated = { ...config };
  if (!updated[theme]) updated[theme] = {};
  if (color && color.trim()) {
    updated[theme].badgeColor = color.trim();
  } else {
    delete updated[theme].badgeColor;
  }
  return updated;
}

export function getBgColor(config, theme) {
  return config?.[theme]?.bgColor || null;
}

export function getFooterColor(config, theme) {
  return config?.[theme]?.footerColor || null;
}

export function getDividerColor(config, theme) {
  return config?.[theme]?.dividerColor || null;
}

export function getBoardColor(config, theme) {
  return config?.[theme]?.boardColor || null;
}

export function setFooterColor(config, theme, color) {
  const updated = { ...config };
  if (!updated[theme]) updated[theme] = {};
  if (color && color.trim()) {
    updated[theme].footerColor = color.trim();
  } else {
    delete updated[theme].footerColor;
  }
  return updated;
}

export function setDividerColor(config, theme, color) {
  const updated = { ...config };
  if (!updated[theme]) updated[theme] = {};
  if (color && color.trim()) {
    updated[theme].dividerColor = color.trim();
  } else {
    delete updated[theme].dividerColor;
  }
  return updated;
}

export function setBoardColor(config, theme, color) {
  const updated = { ...config };
  if (!updated[theme]) updated[theme] = {};
  if (color && color.trim()) {
    updated[theme].boardColor = color.trim();
  } else {
    delete updated[theme].boardColor;
  }
  return updated;
}

export function setBgColor(config, theme, color) {
  const updated = { ...config };
  if (!updated[theme]) updated[theme] = {};
  if (color && color.trim()) {
    updated[theme].bgColor = color.trim();
  } else {
    delete updated[theme].bgColor;
  }
  return updated;
}

export function hexToRgba(hex, alpha = 1) {
  if (!hex) return `rgba(100,116,139,${alpha})`;
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Kept for backward compatibility
export const SELISE_CATEGORY_COLORS = CATEGORY_COLORS;
export function getSeliseCategoryColor(category) {
  return getCategoryColor(category);
}